/* eslint-disable @typescript-eslint/no-non-null-assertion */

import type {ImplicitArrayBuffer, WithImplicitCoercion} from 'buffer';
import type {exec, spawn} from 'child_process';
import type {randomUUID} from 'crypto';
import type {
  access,
  close,
  copyFileSync,
  existsSync,
  mkdirSync,
  open,
  read,
  readdir,
  readdirSync,
  readFileSync,
  renameSync,
  rmSync,
  StatSyncFn,
  symlinkSync,
  unlinkSync,
  writeFileSync,
} from 'fs';
import type {arch, platform, tmpdir, type, userInfo} from 'os';
import type {PlatformPath} from 'path';

declare const autocutPreload: {
  childProcess: {
    spawn: typeof spawn;
    powershell: (command: string) => Promise<{
      stdout: string;
      stderr: string;
      output: number | string | null;
    }>;
    node_exec_async: typeof exec.__promisify__;
    sudo_exec: (command: string) => Promise<boolean>;
  };
  fs: {
    access: typeof access;
    existsSync: typeof existsSync;
    mkdirSync: typeof mkdirSync;
    readdir: typeof readdir;
    readdirSync: typeof readdirSync;
    statSync: StatSyncFn;
    lstatSync: StatSyncFn;
    isFile: (filePath: string) => boolean;
    unlinkSync: typeof unlinkSync;
    writeFileSync: typeof writeFileSync;
    readFileSync: typeof readFileSync;
    open: typeof open;
    read: typeof read;
    close: typeof close;
    Buffer: BufferConstructor;
    rmSync: typeof rmSync;
    symlinkSync: typeof symlinkSync;
    renameSync: typeof renameSync;
    copyFileSync: typeof copyFileSync;
    getPathAsDataUrl: (filePath: string) => string;
  };
  os: {
    homedir: () => any;
    tmpdir: typeof tmpdir;
    platform: typeof platform;
    type: typeof type;
    arch: typeof arch;
    userInfo: typeof userInfo;
  };
  path: {
    join: (...paths: string[]) => string;
    joinUrl: (base: string, ...args: string[]) => string;
    basename: (path: string, suffix?: string) => string;
    dirname: (path: string) => string;
    posix: PlatformPath;
    extname: (path: string) => string;
  };
  electron: {
    getVersion: () => string;
    openLinkInBrowser: (url: string) => Promise<void>;
    reload: () => void;
    getPath: (name: string) => any;
    getClipboard: () => string;
    setClipboard: (text: string) => void;
    minimize: () => void;
    togglePinWindow: () => boolean;
    getSharedVariables: () => {
      FRONT_URL: string;
      COMPUTE_URL: string;
      HOST_SERVER_URL: string;
      API_URL: string;
      WEBSITE_URL: string;
      WEBAPP_URL: string;
      ACADEMY_URL: string;
      PPRO_DOWNLOAD_URL: string;
      DAVINCI_DOWNLOAD_URL: string;
      UPDATER_URL: string;
      RESOURCES_PATH: string;
      TEMP_FOLDER_PATH: string;
      HOME_DIR_PATH: string;
      LOG_FILE_PATH: string;
      TAR_PATH: string;
    };
    getIPInfos: () => {
      ip: string;
      country: string;
      cc: string;
    };
    loadDevUrl: () => void;
  };
  onboarding: {
    redirectToFront: () => void;
    complete: () => void;
    pingFront: () => Promise<unknown>;
  };
  https: {
    redirectToFront: () => void;
    pingFront: () => Promise<unknown>;
  };
  computeNodeIpc: {
    startTask: <T>(task: ComputeTask) => {
      requestId: string;
      promise: Promise<T>;
    };
    killTasks: <T>(taskIds: string[]) => {
      requestId: string;
      promise: Promise<T>;
    };
    killAllTasks: <T>() => {
      requestId: string;
      promise: Promise<T>;
    };
    getTasks: <T>() => {
      requestId: string;
      promise: Promise<T>;
    };
    getTaskProgress: (requestId: string) => {
      requestId: string;
      promise: Promise<ProgressStep[]>;
    };
    connect: () => Promise<void>;
    isConnected: () => boolean;
    setVerboseLevel: (verboseLevel: number) => number;
  };
  hostNodeIpc: {
    request: <T>(
      routeName: string,
      args: any,
    ) => {
      requestId: string;
      promise: Promise<T>;
    };
    connect: () => Promise<void>;
    isConnected: () => boolean;
    setVerboseLevel: (verboseLevel: number) => number;
  };
  ffmpegSetup: {
    getFFMPEGPath: () => string | false;
    downloadFFMPEG: () => any;
  };
  resources: {
    download: (
      url: string,
      options?: {
        outputDir?: string;
      },
    ) => Promise<any>;
    unzip: (filePath: string, unzipPath?: string) => Promise<void>;
  };
  nodeCrypto: {
    verifySignature: (
      stringToSign: string,
      publicKey: string,
      signature: string,
    ) => boolean;
    randomUUID: typeof randomUUID;
  };
  updater: {
    getInfos: () => UpdateInfo;
    quitAndInstall: () => void;
    checkForUpdates: () => void;
  };
  logger: {
    initLogger: (logFilePath: string) => void;
    logFunction: (
      defaultFunc: (...args: any[]) => void,
      logifyFunc: (...args: any[]) => void,
    ) => (...args: [any]) => void;
    log: (...args: any[]) => string;
    info: (...args: any[]) => string;
    warn: (...args: any[]) => string;
    error: (...args: any[]) => string;
    logHeader: string;
    infoHeader: string;
    warnHeader: string;
    errorHeader: string;
    waitForLoggerDrain: () => Promise<void>;
  };
  buffer: {
    from: {
      (array: WithImplicitCoercion<ArrayLike<number>>): Buffer<ArrayBuffer>;
      <TArrayBuffer extends WithImplicitCoercion<ArrayBufferLike>>(
        arrayBuffer: TArrayBuffer,
        byteOffset?: number,
        length?: number,
      ): Buffer<ImplicitArrayBuffer<TArrayBuffer>>;
      (
        string: WithImplicitCoercion<string>,
        encoding?: BufferEncoding,
      ): Buffer<ArrayBuffer>;
    };
    subarray: (
      buffer: Buffer,
      start: number,
      end?: number,
    ) => Buffer<ArrayBufferLike>;
    concat: (
      list: readonly Uint8Array[],
      totalLength?: number,
    ) => Buffer<ArrayBuffer>;
    alloc: (
      size: number,
      fill?: string | Uint8Array | number,
      encoding?: BufferEncoding,
    ) => Buffer<ArrayBuffer>;
    encodeToBase64: (buffer: Uint8Array[]) => string;
  };
  sudo: {
    createFolder: (folderPath: string) => Promise<boolean>;
    unzip: (filePath: string, unzipPath?: string) => Promise<boolean>;
  };
  cookies: {
    getCookie: <T>(name: string) => Promise<T | undefined>;
    setCookie: (name: string, value: any) => Promise<any>;
    getAllCookies: () => Promise<{
      [key: string]: any;
    }>;
  };
};
export type ComputeTask = {
  domain: {
    slug: string;
    target: string;
  };
  user?: {
    key: string;
    tokens?: {
      accessToken: string;
      refreshToken: string;
    };
  };
  priority: number;
  data?: any;
  debug?: boolean;
  selectedSections?: number[][];
  deactivatedTrackIndexes?: number[];
  timelineId?: string;
};
export type ProgressStep = {
  id: string;
  progress: {
    finished: boolean;
    currentStep: number;
    nbSteps: number;
  };
};
export type UpdateInfo = {
  status: 'checking' | 'downloading' | 'ready' | 'error' | 'up-to-date';
  releaseNotes?: string;
  releaseName?: string;
};

declare global {
  interface Window {
    __autocut_preload__: typeof autocutPreload;
    __electron_preload__: typeof autocutPreload;
  }

  type Preload = typeof autocutPreload;
}

let preloadReady = false;

const getPreloadProxy = (prefix: string) => {
  return new Proxy(
    {},
    {
      get(_, prop: string) {
        const path = `${prefix}.${prop}`;
        return new Proxy(() => {}, {
          apply() {
            console.warn(`Calling ${path} before preload is ready`);
            return undefined;
          },
        });
      },
    },
  );
};

export const preload = new Proxy(
  {
    ...window['__autocut_preload__']!,
  },
  {
    get(_, prop: string) {
      const notReadyErrorMessage = `Preload not ready ! Ensure you've called "waitForPreload" before using "preload.${prop}" functions or manage the undefined case with "isPreloadReady"`;
      const notFoundErrorMessage = `"preload.${prop}" does not exist`;

      if (!preloadReady) {
        const stack = new Error().stack;
        console.error(`${notReadyErrorMessage}\n\nCall stack:\n${stack}`);
        if (window.location.hostname === 'localhost') {
          //Error only in dev mode
          throw new Error(notReadyErrorMessage);
        } else {
          return getPreloadProxy(prop);
        }
      }

      const res = window[`__autocut_preload__`]?.[prop as keyof Preload];
      if (res) return res;

      // TODO : Remove this after 01/08/2025 (in preloadTypesTemplate.ts of AEA project)
      const legacyRes = window[`__electron_preload__${prop}` as keyof Window];
      console.debug(
        'window.__electron_preload__.',
        prop,
        !!legacyRes,
        legacyRes,
      );
      if (legacyRes) {
        console.warn(`Use legacy preload, AEA must be updated !`);
        return legacyRes;
      }

      const stack = new Error().stack;
      console.warn(`${notFoundErrorMessage}\n\nCall stack:\n${stack}`);

      return getPreloadProxy(prop);
    },
  },
) as Preload;

export const isPreloadReady = () => preloadReady;

export const waitForPreload = async (source?: string) =>
  new Promise<void>(async (resolve, reject) => {
    const checkPreload = () => {
      try {
        const res = window?.['__autocut_preload__']?.os?.platform();
        const legacyRes =
          //@ts-ignore
          window?.['__electron_preload__os']?.platform();
        return !!res || !!legacyRes;
      } catch {
        return false;
      }
    };

    let i = 0;
    while (i < 100 && !checkPreload()) {
      await new Promise(resolve => setTimeout(resolve, 100));
      i++;
    }
    if (i === 100) {
      console.error('waitForPreload fail ' + (source || ''));
      reject(new Error('Failed to load preload'));
    } else {
      console.log('Preload ready ' + (source || ''));
      preloadReady = true;
      resolve();
    }
  });
